/**
 * AnalysisOptionsForm
 *
 * Shared form component for configuring analysis options.
 * Used by both AnalysisModal (Library screen) and the inline
 * analysis modal in ChatScreenEnhanced.
 */

import React from 'react';
import { View, Text, TouchableOpacity, TextInput, Switch } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useTranslation } from '../../hooks/useTranslation';
import { InfoHint } from '../tutorial/InfoHint';
import { Card, CardContent } from '../ui/card';
import { ICF_FORMS } from '../../data/icfFormStatements';
import type { AnalysisMode, ICFLevel } from '../../types';
import type { AnalysisTierAccess } from '../../utils/planContext';
import { brand } from '../../design-system/theme/tokens';

// Design tokens matching TranscriptionModal / AnalysisModal
const COLORS = {
    primary: brand.primary,
    secondary: '#F59E0B',
    success: '#10B981',
    error: '#EF4444',
    background: '#FFFFFF',
    surface: '#F9FAFB',
    text: {
        primary: '#111827',
        secondary: '#4B5563',
        muted: '#9CA3AF',
    },
    border: '#E5E7EB',
};

export interface AnalysisOptionsFormProps {
    analysisMode: AnalysisMode;
    onAnalysisModeChange: (mode: AnalysisMode) => void;
    requestedLevel: ICFLevel;
    onRequestedLevelChange: (level: ICFLevel) => void;
    includeMarkerTable: boolean;
    onIncludeMarkerTableChange: (value: boolean) => void;
    customPrompt: string;
    onCustomPromptChange: (value: string) => void;
    language: 'tr' | 'en';
    onLanguageChange: (lang: 'tr' | 'en') => void;
    includeInIcfProgress: boolean;
    onIncludeInIcfProgressChange: (value: boolean) => void;
    tierAccess: AnalysisTierAccess;
    showIcfProgressToggle?: boolean;
    /**
     * Controls the level picker + marker-table block independently of the ICF
     * progress toggle. Defaults to `showIcfProgressToggle` so existing callers
     * keep their current behaviour; engagement mode hides only the toggle.
     */
    showLevelSection?: boolean;
    /** D17: Tier 3 renames the picker to "Observation level" and explains the SOF. */
    isTier3?: boolean;
    /** Mentor's active engagements — rendered as the optional "Add to engagement" select. */
    engagementOptions?: EngagementPickerOption[];
    selectedEngagementId?: number | null;
    onSelectedEngagementChange?: (engagementId: number | null) => void;
    /** Opens the upgrade paywall when a locked feature is tapped. */
    onUpgradePress?: (requiredTier: 'tier2' | 'tier3', featureKey: string) => void;
    disabled?: boolean;
    showAdvanced?: boolean;
}

export interface EngagementPickerOption {
    id: number;
    mentee_name: string;
    target_level: Exclude<ICFLevel, 'auto'>;
    /** Revision stamp of the level's official Session Observation Form. */
    sof_revision: string;
}

interface LevelOption {
    value: ICFLevel;
    labelKey: string;
}

/** Official Session Observation Form revisions — PCC's SOF is 07.02.26, not 06.18.26. */
const SOF_REVISION_BY_LEVEL: Record<'acc' | 'pcc' | 'mcc', string> = {
    acc: ICF_FORMS.acc.sof_revision,
    pcc: ICF_FORMS.pcc.sof_revision,
    mcc: ICF_FORMS.mcc.sof_revision,
};

const LEVEL_OPTIONS: LevelOption[] = [
    { value: 'auto', labelKey: 'analysis.modal.levelAuto' },
    { value: 'acc', labelKey: 'analysis.modal.levelACC' },
    { value: 'pcc', labelKey: 'analysis.modal.levelPCC' },
    { value: 'mcc', labelKey: 'analysis.modal.levelMCC' },
];

function LockedOverlay({ label, onPress }: { label: string; onPress?: () => void }) {
    return (
        <TouchableOpacity
            activeOpacity={0.8}
            onPress={onPress}
            disabled={!onPress}
            style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(255,255,255,0.5)',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 8,
                zIndex: 2,
            }}
        >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                <MaterialIcons name="lock" size={14} color={COLORS.secondary} />
                <Text style={{ fontSize: 12, fontWeight: '600', color: COLORS.secondary }}>
                    {label}
                </Text>
            </View>
        </TouchableOpacity>
    );
}

export function AnalysisOptionsForm({
    analysisMode,
    onAnalysisModeChange,
    requestedLevel,
    onRequestedLevelChange,
    includeMarkerTable,
    onIncludeMarkerTableChange,
    customPrompt,
    onCustomPromptChange,
    language,
    onLanguageChange,
    includeInIcfProgress,
    onIncludeInIcfProgressChange,
    tierAccess,
    showIcfProgressToggle = true,
    showLevelSection,
    isTier3 = false,
    engagementOptions = [],
    selectedEngagementId = null,
    onSelectedEngagementChange,
    onUpgradePress,
    disabled = false,
    showAdvanced = true,
}: AnalysisOptionsFormProps) {
    const { t } = useTranslation();

    const upgradeLabel = t('analysis.modal.upgradeRequired') || 'Upgrade';
    const levelSectionVisible = showLevelSection ?? showIcfProgressToggle;
    const selectedEngagement = engagementOptions.find((item) => item.id === selectedEngagementId) || null;
    // D17: an engagement pins the level to its target, so the picker is read-only.
    const levelLocked = Boolean(selectedEngagement);
    const observationLevel = requestedLevel === 'auto' ? null : requestedLevel.toUpperCase();

    return (
        <View style={{ gap: 16 }}>
            <InfoHint id="analysis_options" />
            {/* ── Mode Selection ── */}
            {!selectedEngagement && showAdvanced && <View>
                <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text.primary, marginBottom: 8 }}>
                    {t('analysis.modal.modeTitle') || 'Analysis Type'}
                </Text>
                <View style={{ flexDirection: 'row', gap: 10 }}>
                    {/* ICF Assessment Card */}
                    <TouchableOpacity
                        style={{
                            flex: 1,
                            padding: 14,
                            borderRadius: 10,
                            borderWidth: 2,
                            borderColor: analysisMode === 'icf' ? COLORS.primary : COLORS.border,
                            backgroundColor: analysisMode === 'icf' ? 'rgba(30, 64, 175, 0.06)' : 'white',
                        }}
                        onPress={() => onAnalysisModeChange('icf')}
                        disabled={disabled}
                        activeOpacity={0.7}
                    >
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                            <MaterialIcons
                                name="verified"
                                size={20}
                                color={analysisMode === 'icf' ? COLORS.primary : COLORS.text.muted}
                            />
                            <Text style={{
                                fontSize: 14,
                                fontWeight: '700',
                                color: analysisMode === 'icf' ? COLORS.primary : COLORS.text.primary,
                                marginLeft: 6,
                            }}>
                                {t('analysis.modal.icfMode') || 'ICF Assessment'}
                            </Text>
                        </View>
                        <Text style={{ fontSize: 12, color: COLORS.text.muted, lineHeight: 17 }}>
                            {t('analysis.modal.icfModeDesc') || 'Evaluate coaching competency against ICF standards'}
                        </Text>
                    </TouchableOpacity>

                    {/* Custom Analysis Card */}
                    <View style={{ flex: 1, position: 'relative' }}>
                        <TouchableOpacity
                            style={{
                                flex: 1,
                                padding: 14,
                                borderRadius: 10,
                                borderWidth: 2,
                                borderColor: analysisMode === 'custom' ? COLORS.primary : COLORS.border,
                                backgroundColor: analysisMode === 'custom' ? 'rgba(30, 64, 175, 0.06)' : 'white',
                                opacity: tierAccess.allowCustomMode ? 1 : 0.5,
                            }}
                            onPress={() => {
                                if (tierAccess.allowCustomMode) {
                                    onAnalysisModeChange('custom');
                                }
                            }}
                            disabled={disabled || !tierAccess.allowCustomMode}
                            activeOpacity={0.7}
                        >
                            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                                <MaterialIcons
                                    name="tune"
                                    size={20}
                                    color={analysisMode === 'custom' ? COLORS.primary : COLORS.text.muted}
                                />
                                <Text style={{
                                    fontSize: 14,
                                    fontWeight: '700',
                                    color: analysisMode === 'custom' ? COLORS.primary : COLORS.text.primary,
                                    marginLeft: 6,
                                }}>
                                    {t('analysis.modal.customMode') || 'Custom Analysis'}
                                </Text>
                            </View>
                            <Text style={{ fontSize: 12, color: COLORS.text.muted, lineHeight: 17 }}>
                                {t('analysis.modal.customModeDesc') || 'Analyze with your own custom instructions'}
                            </Text>
                        </TouchableOpacity>
                        {!tierAccess.allowCustomMode && (
                            <LockedOverlay
                                label={upgradeLabel}
                                onPress={onUpgradePress ? () => onUpgradePress('tier3', 'customMode') : undefined}
                            />
                        )}
                    </View>
                </View>
            </View>}

            {/* ── Language Selection ── */}
            <View>
                <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text.primary, marginBottom: 8 }}>
                    {t(selectedEngagement ? 'analysis.modal.feedbackLanguage' : 'analysis.modal.language')}
                </Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                    {([
                        { value: 'tr' as const, label: t('transcription.modal.turkish') || 'Turkish' },
                        { value: 'en' as const, label: t('transcription.modal.english') || 'English' },
                    ]).map((option) => (
                        <TouchableOpacity
                            key={option.value}
                            onPress={() => onLanguageChange(option.value)}
                            disabled={disabled}
                            style={{
                                flex: 1,
                                paddingVertical: 10,
                                paddingHorizontal: 12,
                                borderRadius: 8,
                                borderWidth: 2,
                                borderColor: language === option.value ? COLORS.primary : COLORS.border,
                                backgroundColor: language === option.value ? 'rgba(30, 64, 175, 0.08)' : 'white',
                                alignItems: 'center',
                            }}
                        >
                            <Text style={{
                                fontSize: 14,
                                fontWeight: language === option.value ? '600' : '500',
                                color: language === option.value ? COLORS.primary : COLORS.text.secondary,
                            }}>
                                {option.label}
                            </Text>
                        </TouchableOpacity>
                    ))}
                </View>
            </View>

            {/* ── ICF Mode Options ── */}
            {!selectedEngagement && showAdvanced && analysisMode === 'icf' && isTier3 && engagementOptions.length > 0 && onSelectedEngagementChange && (
                <View>
                    <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text.primary, marginBottom: 4 }}>
                        {t('analysis.modal.engagementSelectTitle')}
                    </Text>
                    <Text style={{ fontSize: 12, color: COLORS.text.muted, marginBottom: 8, lineHeight: 17 }}>
                        {t('analysis.modal.engagementSelectHelp')}
                    </Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                        {[null, ...engagementOptions.map((item) => item.id)].map((optionId) => {
                            const option = engagementOptions.find((item) => item.id === optionId) || null;
                            const isSelected = selectedEngagementId === optionId;
                            return (
                                <TouchableOpacity
                                    key={optionId === null ? 'none' : optionId}
                                    testID={`analysis_engagement_option_${optionId === null ? 'none' : optionId}`}
                                    onPress={() => onSelectedEngagementChange(optionId)}
                                    disabled={disabled}
                                    style={{
                                        paddingVertical: 8,
                                        paddingHorizontal: 14,
                                        borderRadius: 10,
                                        borderWidth: 2,
                                        borderColor: isSelected ? COLORS.primary : COLORS.border,
                                        backgroundColor: isSelected ? 'rgba(30, 64, 175, 0.08)' : 'white',
                                    }}
                                >
                                    <Text style={{
                                        fontSize: 13,
                                        fontWeight: isSelected ? '700' : '500',
                                        color: isSelected ? COLORS.primary : COLORS.text.secondary,
                                    }}>
                                        {option
                                            ? `${option.mentee_name} · ${option.target_level.toUpperCase()}`
                                            : t('analysis.modal.engagementSelectNone')}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </View>
                    {selectedEngagement && (
                        <Text style={{ fontSize: 12, color: COLORS.text.secondary, marginTop: 8, lineHeight: 17 }}>
                            {t('analysis.modal.engagementSelectLocked', {
                                level: selectedEngagement.target_level.toUpperCase(),
                            })}
                        </Text>
                    )}
                </View>
            )}

            {showAdvanced && !selectedEngagement && analysisMode === 'icf' && levelSectionVisible && (
                <View style={{ gap: 14 }}>
                    {/* Level Selection */}
                    <View style={{ position: 'relative' }}>
                        <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text.primary, marginBottom: isTier3 ? 4 : 8 }}>
                            {isTier3
                                ? t('analysis.modal.observationLevelTitle')
                                : (t('analysis.modal.levelTitle') || 'Target Level')}
                        </Text>
                        {isTier3 && (
                            <Text style={{ fontSize: 12, color: COLORS.text.muted, marginBottom: 8, lineHeight: 17 }}>
                                {observationLevel
                                    ? t('analysis.modal.observationLevelHelp', {
                                        level: observationLevel,
                                        revision: selectedEngagement?.sof_revision
                                            || SOF_REVISION_BY_LEVEL[requestedLevel as 'acc' | 'pcc' | 'mcc'],
                                    })
                                    : t('analysis.modal.observationLevelHelpAuto')}
                            </Text>
                        )}
                        <View style={{
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                            gap: 8,
                            opacity: tierAccess.allowLevelSelect ? 1 : 0.5,
                        }}>
                            {LEVEL_OPTIONS.map((opt) => {
                                const isSelected = requestedLevel === opt.value;
                                const isAutoOption = opt.value === 'auto';
                                const optionDisabled = disabled
                                    || levelLocked
                                    || (!tierAccess.allowLevelSelect && !isAutoOption);
                                return (
                                    <TouchableOpacity
                                        key={opt.value}
                                        testID={`analysis_level_option_${opt.value}`}
                                        onPress={() => {
                                            if (!levelLocked && (tierAccess.allowLevelSelect || isAutoOption)) {
                                                onRequestedLevelChange(opt.value);
                                            }
                                        }}
                                        disabled={optionDisabled}
                                        style={{
                                            opacity: levelLocked && !isSelected ? 0.45 : 1,
                                            paddingVertical: 8,
                                            paddingHorizontal: 16,
                                            borderRadius: 20,
                                            borderWidth: 2,
                                            borderColor: isSelected ? COLORS.primary : COLORS.border,
                                            backgroundColor: isSelected ? 'rgba(30, 64, 175, 0.08)' : 'white',
                                        }}
                                    >
                                        <Text style={{
                                            fontSize: 13,
                                            fontWeight: isSelected ? '700' : '500',
                                            color: isSelected ? COLORS.primary : COLORS.text.secondary,
                                        }}>
                                            {isAutoOption && isTier3
                                                ? t('analysis.modal.observationLevelAuto')
                                                : (t(opt.labelKey) || opt.value.toUpperCase())}
                                        </Text>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>
                        {!tierAccess.allowLevelSelect && (
                            <TouchableOpacity
                                onPress={onUpgradePress ? () => onUpgradePress('tier2', 'levelSelect') : undefined}
                                disabled={!onUpgradePress}
                                activeOpacity={0.7}
                                style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    marginTop: 6,
                                    gap: 4,
                                }}
                            >
                                <MaterialIcons name="lock" size={13} color={COLORS.secondary} />
                                <Text style={{ fontSize: 12, color: COLORS.secondary, fontWeight: '500' }}>
                                    {upgradeLabel}
                                </Text>
                            </TouchableOpacity>
                        )}
                    </View>

                    {/* Marker Table Toggle */}
                    <View style={{ position: 'relative' }}>
                        <Card style={{
                            backgroundColor: tierAccess.allowMarkerTable ? '#F0F9FF' : COLORS.surface,
                            borderColor: tierAccess.allowMarkerTable ? '#BAE6FD' : COLORS.border,
                            opacity: tierAccess.allowMarkerTable ? 1 : 0.5,
                        }}>
                            <CardContent style={{ padding: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                <View style={{ flex: 1, marginRight: 12 }}>
                                    <Text style={{ fontSize: 14, fontWeight: '600', color: COLORS.text.primary, marginBottom: 2 }}>
                                        {t('analysis.modal.includeMarkerTable') || 'Include Full MSR Table'}
                                    </Text>
                                    <Text style={{ fontSize: 12, color: COLORS.text.muted }}>
                                        {t('analysis.modal.includeMarkerTableDesc') || 'Evaluate every MSR individually with transcript evidence (takes longer)'}
                                    </Text>
                                </View>
                                <Switch
                                    value={includeMarkerTable}
                                    onValueChange={onIncludeMarkerTableChange}
                                    trackColor={{ false: '#E5E7EB', true: '#93c5fd' }}
                                    thumbColor={includeMarkerTable ? COLORS.primary : '#9CA3AF'}
                                    disabled={disabled || !tierAccess.allowMarkerTable}
                                />
                            </CardContent>
                        </Card>
                        {!tierAccess.allowMarkerTable && (
                            <TouchableOpacity
                                onPress={onUpgradePress ? () => onUpgradePress('tier2', 'markerTable') : undefined}
                                disabled={!onUpgradePress}
                                activeOpacity={0.7}
                                style={{
                                    position: 'absolute',
                                    top: 8, right: 14,
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 4,
                                    zIndex: 2,
                                }}
                            >
                                <MaterialIcons name="lock" size={13} color={COLORS.secondary} />
                                <Text style={{ fontSize: 11, color: COLORS.secondary, fontWeight: '600' }}>
                                    {upgradeLabel}
                                </Text>
                            </TouchableOpacity>
                        )}
                    </View>
                </View>
            )}

            {/* ── Custom Mode Options ── */}
            {showAdvanced && (selectedEngagement || analysisMode === 'custom') && (
                <View>
                    <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text.primary, marginBottom: 8 }}>
                        {t('analysis.modal.instructions') || 'Analysis Instructions'}
                    </Text>
                    <TextInput
                        style={{
                            backgroundColor: 'white',
                            borderRadius: 8,
                            padding: 12,
                            fontSize: 14,
                            minHeight: 100,
                            borderWidth: 1,
                            borderColor: COLORS.border,
                            textAlignVertical: 'top',
                            color: COLORS.text.primary,
                        }}
                        value={customPrompt}
                        onChangeText={onCustomPromptChange}
                        placeholder={t('analysis.modal.customPromptPlaceholder') || 'Enter your custom analysis instructions...'}
                        placeholderTextColor={COLORS.text.muted}
                        multiline
                        numberOfLines={5}
                        editable={!disabled}
                    />
                </View>
            )}

            {/* ── ICF Progress Toggle (ICF mode only) ── */}
            {showAdvanced && !selectedEngagement && analysisMode === 'icf' && showIcfProgressToggle && (
                <Card style={{ backgroundColor: '#F0F9FF', borderColor: '#BAE6FD' }}>
                    <CardContent style={{ padding: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <View style={{ flex: 1, marginRight: 12 }}>
                            <Text style={{ fontSize: 14, fontWeight: '600', color: COLORS.text.primary, marginBottom: 2 }}>
                                {t('transcription.modal.icfProgressToggle')}
                            </Text>
                            <Text style={{ fontSize: 12, color: COLORS.text.muted }}>
                                {t('transcription.modal.icfProgressDesc')}
                            </Text>
                        </View>
                        <Switch
                            testID="analysis_icf_progress_toggle"
                            accessibilityRole="switch"
                            accessibilityState={{ checked: includeInIcfProgress, disabled }}
                            value={includeInIcfProgress}
                            onValueChange={onIncludeInIcfProgressChange}
                            trackColor={{ false: '#E5E7EB', true: '#93c5fd' }}
                            thumbColor={includeInIcfProgress ? COLORS.primary : '#9CA3AF'}
                            disabled={disabled}
                        />
                    </CardContent>
                </Card>
            )}

            {/* ── Info Note ── */}
            {showAdvanced && !selectedEngagement && <View style={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                padding: 12,
                backgroundColor: analysisMode === 'icf' ? '#FEF3C7' : '#F0F9FF',
                borderRadius: 8,
            }}>
                <MaterialIcons
                    name="info"
                    size={18}
                    color={analysisMode === 'icf' ? COLORS.secondary : COLORS.primary}
                    style={{ marginRight: 8, marginTop: 1 }}
                />
                <Text style={{ flex: 1, fontSize: 12, color: COLORS.text.secondary, lineHeight: 17 }}>
                    {analysisMode === 'icf'
                        ? (t('analysis.modal.infoNote') || 'The analysis will use our AI coaching knowledge base to provide structured feedback on the coaching session.')
                        : (t('analysis.modal.customModeDesc') || 'Analyze with your own custom instructions')}
                </Text>
            </View>}
        </View>
    );
}

export default AnalysisOptionsForm;
